import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, MapPin, Calendar, ExternalLink } from 'lucide-react';
import './Footer.css';

const Footer = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Services',
      links: [
        { label: 'ASMA - Enterprise Automation', href: 'https://asma.astral-ai.io', external: true },
        { label: 'GAIA - Mystical Guidance', href: 'https://gaia.astral-ai.io', external: true },
        { label: 'Custom AI Solutions', href: '#services' },
        { label: 'Data Science Consulting', href: '#services' },
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '#about' },
        { label: 'Technology Stack', href: '#tech-stack' },
        { label: 'Careers', href: 'mailto:contact@astral-ai.io' },
      ]
    }
  ];



  const contactInfo = [
    { icon: Mail, text: 'contact@astral-ai.io', href: 'mailto:contact@astral-ai.io' },
    { icon: MapPin, text: 'Dubai, UAE', href: '#' },
    { icon: Calendar, text: 'Available 24/7', href: '#' },
  ];

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <footer className="footer-modern" ref={ref}>
      <div className="footer-background">
        <div className="footer-gradient"></div>
        <div className="footer-grid-pattern"></div>
      </div>

      <motion.div 
        className="footer-container"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        {/* Header Section */}
        <motion.div className="footer-header" variants={itemVariants}>
          <div className="footer-brand">
            <img src="/logo.png" alt="Astral AI" className="footer-logo" />
            <div className="brand-info">
              <h3 className="footer-brand-name">ASTRAL AI</h3>
              <p className="footer-tagline">
                Pioneering the future of intelligent automation and mystical guidance
              </p>
            </div>
          </div>
          
          <div className="footer-contact">
            {contactInfo.map((contact, index) => (
              <motion.a
                key={index}
                href={contact.href}
                className="contact-item"
                variants={itemVariants}
                whileHover={{ scale: 1.05, x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <contact.icon size={16} />
                <span>{contact.text}</span>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="footer-main">
          <div className="footer-sections">
            {footerSections.map((section, sectionIndex) => (
              <motion.div 
                key={section.title} 
                className="footer-section"
                variants={itemVariants}
              >
                <h4 className="section-title">{section.title}</h4>
                <div className="section-links">
                  {section.links.map((link, linkIndex) => (
                    <motion.a
                      key={linkIndex}
                      href={link.href}
                      className="footer-link"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <span>{link.label}</span>
                      {link.external && <ExternalLink size={12} />}
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Newsletter Signup */}
          <motion.div className="footer-newsletter" variants={itemVariants}>
            <h4 className="newsletter-title">Stay Connected</h4>
            <p className="newsletter-description">
              Get the latest updates on AI automation and mystical insights
            </p>
            <div className="newsletter-form">
              <input 
                type="email" 
                placeholder="Enter your email address"
                className="newsletter-input"
              />
              <motion.button 
                className="newsletter-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Subscribe
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* Footer Bottom */}
        <motion.div className="footer-bottom" variants={itemVariants}>
          <div className="footer-bottom-left">
            <p className="copyright">
              © {currentYear} Astral AI. All rights reserved.
            </p>
            <div className="technology-badge">
              <span>Powered by Advanced AI</span>
            </div>
          </div>

          <div className="footer-bottom-right">
            <motion.button 
              className="back-to-top"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Top
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </footer>
  );
};

export default Footer; 